import { ReactNode, useState } from "react";
import { Divider, Form, Input, notification } from "antd";
import { commands } from "@uiw/react-md-editor";

import { MarkdownField } from "@/ui-components/commonComponents/MarkdownField/MarkdownField";
import { getFriendlyErrorMessage } from "@/shared/services/http";
import { localeLabel, resolveEffectiveLocales } from "@/shared/utils/locale.utils";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import { InfoNote } from "@/shared/components/InfoNote/InfoNote";

import { Button, ButtonSize } from "@/ui-components/commonComponents/Button/Button";

import { ReactComponent as SaveSVG } from "@/assets/icons/feather-icons/save.svg";

import { ScaleAnalysesHttpService } from "./scale-analyses-http.service";
import { ConvertToTestSpecificModal } from "./ConvertToTestSpecificModal";
import {
  IDevelopmentAnalysis,
  IDevelopmentAnalysisSection,
  IOverallAnalysisContent,
  IScaleAnalysisReport,
  IUpdateScaleAnalysisPayload,
  LocalizedText,
} from "./scale-analyses.types";

import localeTabStyle from "@/shared/styles/localeTabs.module.css";
import style from "./style.module.css";

const OVERALL_BANDS: { key: keyof IOverallAnalysisContent; label: string }[] = [
  { key: "high", label: "High" },
  { key: "medium", label: "Medium" },
  { key: "low", label: "Low" },
];

const DEV_SECTIONS: {
  key: keyof IDevelopmentAnalysis;
  descriptionLabel: string;
  tipLabels: [string, string];
}[] = [
  { key: "top_strength", descriptionLabel: "Top Description", tipLabels: ["Top Tip 1", "Top Tip 2"] },
  {
    key: "developing",
    descriptionLabel: "Developing Description",
    tipLabels: ["Developing Tip 1", "Developing Tip 2"],
  },
  {
    key: "opportunity",
    descriptionLabel: "Opportunity Description",
    tipLabels: ["Opportunity Tip 1", "Opportunity Tip 2"],
  },
];

const MARKDOWN_TOOLBAR = { commands: [commands.bold, commands.italic], extraCommands: [], preview: "edit" as const };

const EMPTY_OVERALL: IOverallAnalysisContent = { high: "", medium: "", low: "" };

/**
 * @param {IDevelopmentAnalysisSection} [stored] - One Develop bucket as the API returned it.
 * @returns {IDevelopmentAnalysisSection} The same bucket with its description and both tips present.
 */
function withCompleteFields(stored?: IDevelopmentAnalysisSection): IDevelopmentAnalysisSection {
  return { description: stored?.description ?? {}, tips: [stored?.tips?.[0] ?? {}, stored?.tips?.[1] ?? {}] };
}

/**
 * Fills each bucket in on its own rather than the section as a whole, so a bucket that arrives short
 * of a description or a tip cannot leave state with a gap the tip setters index straight into.
 *
 * @param {Partial<IDevelopmentAnalysis> | null} stored - Develop content as the API returned it.
 * @returns {IDevelopmentAnalysis} Every bucket, each carrying a description and exactly two tips.
 */
function withCompleteBuckets(stored: Partial<IDevelopmentAnalysis> | null): IDevelopmentAnalysis {
  return {
    top_strength: withCompleteFields(stored?.top_strength),
    developing: withCompleteFields(stored?.developing),
    opportunity: withCompleteFields(stored?.opportunity),
  };
}

/**
 * @param {string} label - The field's visible label.
 * @returns {string} The message shown under that field when it is left unauthored.
 */
function requiredMessage(label: string): string {
  return `${label} is required.`;
}

/**
 *
 * @param {LocalizedText} text - Locale-keyed text straight from the form state.
 * @returns {LocalizedText} The same map without the locales left empty.
 */
function withoutBlankLocales(text: LocalizedText): LocalizedText {
  return Object.fromEntries(Object.entries(text).filter(([, value]) => value.trim() !== ""));
}

/**
 * Tolerates an absent map for the same reason the fields render through `?.`: storage predates this
 * editor's shape rules, so a bucket can arrive without its description or with fewer than two tips.
 *
 * @param {LocalizedText} [text] - Locale-keyed text straight from the form state.
 * @returns {boolean} True when at least one locale carries non-blank text.
 */
function hasSomeContent(text?: LocalizedText): boolean {
  return Object.values(text ?? {}).some((value) => value.trim() !== "");
}

/**
 * Reports which Overall bands are unauthored, keyed by field id.
 *
 * A section is written whole, and the API rejects a blank band because the report renders whichever
 * band the candidate's score selects without a guard. Checking here turns that into an inline message
 * on the offending field instead of a rejected request.
 *
 * @param {object} args - The band content to check and the id prefix its fields carry.
 * @returns {Record<string, string>} Field id to message, empty when every band is authored.
 */
function findBlankOverallFields({
  overall,
  idPrefix,
}: {
  overall: IOverallAnalysisContent;
  idPrefix: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  OVERALL_BANDS.forEach(({ key, label }) => {
    if ((overall[key] ?? "").trim() === "") {
      errors[`${idPrefix}-overall-${key}`] = requiredMessage(label);
    }
  });
  return errors;
}

/**
 * Reports which Develop fields are unauthored in every locale, keyed by field id.
 *
 * A field only has to be authored in one locale — the report falls back across locales, and the API
 * requires no more than that — so a field left blank on the active locale is not an error as long as
 * another locale carries it.
 *
 * @param {object} args - The Develop content to check and the id prefix its fields carry.
 * @returns {Record<string, string>} Field id to message, empty when every field is authored.
 */
function findBlankDevelopFields({
  development,
  idPrefix,
}: {
  development: IDevelopmentAnalysis;
  idPrefix: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  DEV_SECTIONS.forEach(({ key, descriptionLabel, tipLabels }) => {
    const section = development[key];
    if (!hasSomeContent(section?.description)) {
      errors[`${idPrefix}-${key}-description`] = requiredMessage(descriptionLabel);
    }
    ([0, 1] as const).forEach((index) => {
      if (!hasSomeContent(section?.tips?.[index])) {
        errors[`${idPrefix}-${key}-tip-${index}`] = requiredMessage(tipLabels[index]);
      }
    });
  });
  return errors;
}

/**
 * Wraps a field in antd's `Form.Item` for its label and error chrome. Values stay in this module's
 * state rather than a `Form` store, so `Form.Item` carries no `name` and needs `htmlFor` to bind the
 * label to the control.
 *
 * @param {object} props - Component props.
 * @param {string} props.id - Id of the control the label points at.
 * @param {string} props.label - Visible label text.
 * @param {string} [props.error] - Message to show beneath the field.
 * @param {ReactNode} props.children - The control itself.
 * @returns {JSX.Element} The wrapped field.
 */
function Field({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) {
  return (
    <Form.Item
      className={style.field}
      label={label}
      htmlFor={id}
      colon={false}
      validateStatus={error ? "error" : undefined}
      help={error ? <span id={`${id}-error`}>{error}</span> : undefined}
    >
      {children}
    </Form.Item>
  );
}

function MarkdownRow({
  id,
  label,
  error,
  value,
  onChange,
}: {
  id: string;
  label: string;
  error?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field id={id} label={label} error={error}>
      <MarkdownField
        height={150}
        {...MARKDOWN_TOOLBAR}
        value={value}
        onChange={(next) => onChange(next ?? "")}
        textareaProps={{ id, "aria-describedby": error ? `${id}-error` : undefined }}
      />
    </Field>
  );
}

/**
 * Language switcher for the Develop Report section — the only localized content in this editor, as
 * the Overall Report is English-only.
 *
 * Deliberately not `LocaleContent`, which hardcodes `forceRender` on every pane so no locale's
 * inputs unmount. That is cheap for its current callers, which put one editor in a pane, but this
 * view puts nine markdown editors plus the display name in each, and the page mounts every scale at
 * once — so a pane per locale would multiply the heaviest control here by the locale count. Holding
 * all locales in state and swapping the active key keeps that at nine however many locales exist,
 * and loses nothing on switch because the values never lived in the inputs.
 *
 * These are toggle buttons rather than tabs: switching swaps which key the same fields read, and
 * there are no panes for a tablist to control.
 */
function LocaleSwitcher({
  locales,
  activeLocale,
  onSelect,
}: {
  locales: string[];
  activeLocale: string;
  onSelect: (locale: string) => void;
}) {
  if (locales.length === 1) {
    return null;
  }

  return (
    <div className={style.localeSelector}>
      {locales.map((locale) => (
        <button
          key={locale}
          type="button"
          aria-pressed={locale === activeLocale}
          className={
            locale === activeLocale
              ? `${localeTabStyle.localePill} ${localeTabStyle.localePillActive}`
              : localeTabStyle.localePill
          }
          onClick={() => onSelect(locale)}
        >
          {localeLabel(locale)}
        </button>
      ))}
    </div>
  );
}

export function ScaleAnalysisEditor({
  scale,
  testId,
  availableLocales,
  onRowChanged,
}: {
  scale: IScaleAnalysisReport;
  testId: number;
  availableLocales: LanguagesIsoEnum[];
  onRowChanged: () => void;
}) {
  // Spread over the empty shapes rather than defaulting on null: storage holds `{}` for a half
  // nobody authored, and `{}` is truthy, so `??` would leave the state — and every save built from
  // it — missing the bands and buckets whose fields the editor is about to render.
  const [overall, setOverall] = useState<IOverallAnalysisContent>({ ...EMPTY_OVERALL, ...scale.overallAnalysis });
  const [development, setDevelopment] = useState<IDevelopmentAnalysis>(withCompleteBuckets(scale.developmentAnalysis));
  const [displayName, setDisplayName] = useState<LocalizedText>(scale.displayName ?? {});
  // Held in state rather than read from the prop so first authoring can adopt the id the create
  // returns. Refetching instead would swap the row id the parent keys this editor on, remounting it
  // and discarding whatever the other section has typed but not yet saved.
  const [scaleAnalysisId, setScaleAnalysisId] = useState<number | null>(scale.scaleAnalysisId);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [savingOverall, setSavingOverall] = useState(false);
  const [savingDevelop, setSavingDevelop] = useState(false);
  const [isConvertOpen, setConvertOpen] = useState(false);
  const [isConverting, setConverting] = useState(false);

  const locales = resolveEffectiveLocales(availableLocales);
  const [activeLocale, setActiveLocale] = useState(locales[0]);
  const hasAnalysisRow = scaleAnalysisId !== null;

  // Several scales render on the page at once, so field ids carry the scale to stay unique.
  const idPrefix = `scale-${scale.scaleId}`;

  const clearError = (fieldId: string) =>
    setErrors((prev) => {
      if (!prev[fieldId]) {
        return prev;
      }
      const { [fieldId]: _cleared, ...rest } = prev;
      return rest;
    });

  const setOverallValue = ({ band, value }: { band: keyof IOverallAnalysisContent; value: string }) => {
    clearError(`${idPrefix}-overall-${band}`);
    setOverall((prev) => ({ ...prev, [band]: value }));
  };

  const setDevDescription = ({
    section,
    locale,
    value,
  }: {
    section: keyof IDevelopmentAnalysis;
    locale: string;
    value: string;
  }) => {
    clearError(`${idPrefix}-${section}-description`);
    setDevelopment((prev) => ({
      ...prev,
      [section]: { ...prev[section], description: { ...prev[section].description, [locale]: value } },
    }));
  };

  const setDevTip = ({
    section,
    index,
    locale,
    value,
  }: {
    section: keyof IDevelopmentAnalysis;
    index: 0 | 1;
    locale: string;
    value: string;
  }) => {
    clearError(`${idPrefix}-${section}-tip-${index}`);
    setDevelopment((prev) => {
      const tips: [LocalizedText, LocalizedText] = [{ ...prev[section].tips[0] }, { ...prev[section].tips[1] }];
      tips[index] = { ...tips[index], [locale]: value };
      return { ...prev, [section]: { ...prev[section], tips } };
    });
  };

  /**
   * Writes one section, creating the scale's row when it has none. Create carries only the section
   * being saved, so authoring the other half later updates the same row rather than starting again.
   *
   * @param {IUpdateScaleAnalysisPayload} content - The section being saved.
   * @returns {Promise<void>} Resolves once the write lands.
   */
  const saveContent = async (content: IUpdateScaleAnalysisPayload): Promise<void> => {
    if (scaleAnalysisId === null) {
      const created = await ScaleAnalysesHttpService.createScaleAnalysis(testId, {
        scaleAbbr: scale.abbr,
        ...content,
      });
      setScaleAnalysisId(created.scaleAnalysisId);
      return;
    }
    await ScaleAnalysesHttpService.updateScaleAnalysis(scaleAnalysisId, content);
  };

  const handleSaveOverall = async () => {
    const blankFields = findBlankOverallFields({ overall, idPrefix });
    if (Object.keys(blankFields).length > 0) {
      setErrors((prev) => ({ ...prev, ...blankFields }));
      notification.error({ message: "Fill in every Overall Report field before saving." });
      return;
    }

    try {
      setSavingOverall(true);
      await saveContent({ overallAnalysis: overall });
      notification.success({ message: "Overall Report saved." });
    } catch (error) {
      notification.error({ message: getFriendlyErrorMessage(error) ?? "Failed to save Overall Report." });
    } finally {
      setSavingOverall(false);
    }
  };

  const handleSaveDevelop = async () => {
    const blankFields = findBlankDevelopFields({ development, idPrefix });
    if (Object.keys(blankFields).length > 0) {
      setErrors((prev) => ({ ...prev, ...blankFields }));
      notification.error({ message: "Fill in every Develop Report field before saving." });
      return;
    }

    setSavingDevelop(true);
    // Both writes are attempted even if one fails, so a failure of either doesn't abandon the other.
    const [analysisResult, displayNameResult] = await Promise.allSettled([
      saveContent({ developmentAnalysis: development }),
      ScaleAnalysesHttpService.updateScaleDisplayName(scale.scaleId, withoutBlankLocales(displayName)),
    ]);
    setSavingDevelop(false);

    const failure = [analysisResult, displayNameResult].find(
      (result): result is PromiseRejectedResult => result.status === "rejected",
    );
    if (failure) {
      notification.error({ message: getFriendlyErrorMessage(failure.reason) ?? "Failed to save Develop Report." });
      return;
    }
    notification.success({ message: "Develop Report saved." });
  };

  const handleConvert = async () => {
    if (scaleAnalysisId === null) {
      return;
    }
    try {
      setConverting(true);
      await ScaleAnalysesHttpService.convertToTestSpecific({ scaleAnalysisId, testId });
      notification.success({ message: `${scale.abbr} is now test specific.` });
      setConvertOpen(false);
      onRowChanged();
    } catch (error) {
      notification.error({ message: getFriendlyErrorMessage(error) ?? "Failed to convert scale to test specific." });
    } finally {
      setConverting(false);
    }
  };

  const saveButton = (onClick: () => void, isLoading: boolean) => (
    <div className={style.saveRow}>
      <Button
        icon={<SaveSVG aria-hidden="true" className={style.saveIcon} />}
        buttonSize={ButtonSize.XSmall}
        isLoading={isLoading}
        onClick={onClick}
      >
        Save
      </Button>
    </div>
  );

  const displayNameId = `${idPrefix}-display-name`;

  const panes = (
    <>
      <section className={style.section}>
        <h3 className={style.sectionTitle}>Overall Report</h3>
        <div className={style.columns}>
          {OVERALL_BANDS.map((band) => {
            const id = `${idPrefix}-overall-${band.key}`;
            return (
              <MarkdownRow
                key={band.key}
                id={id}
                label={band.label}
                error={errors[id]}
                value={overall[band.key] ?? ""}
                onChange={(value) => setOverallValue({ band: band.key, value })}
              />
            );
          })}
        </div>
        {saveButton(handleSaveOverall, savingOverall)}
      </section>

      <section className={style.section}>
        <h3 className={style.sectionTitle}>Develop Report</h3>

        <LocaleSwitcher locales={locales} activeLocale={activeLocale} onSelect={setActiveLocale} />

        <Field id={displayNameId} label="Scale Display Name">
          <Input
            id={displayNameId}
            value={displayName[activeLocale] ?? ""}
            placeholder="Scale display name"
            onChange={(e) => setDisplayName((prev) => ({ ...prev, [activeLocale]: e.target.value }))}
          />
        </Field>

        {DEV_SECTIONS.map((section) => {
          const descriptionId = `${idPrefix}-${section.key}-description`;
          return (
            <div className={style.columns} key={section.key}>
              <MarkdownRow
                id={descriptionId}
                label={section.descriptionLabel}
                error={errors[descriptionId]}
                value={development[section.key]?.description?.[activeLocale] ?? ""}
                onChange={(value) => setDevDescription({ section: section.key, locale: activeLocale, value })}
              />
              {([0, 1] as const).map((index) => {
                const tipId = `${idPrefix}-${section.key}-tip-${index}`;
                return (
                  <MarkdownRow
                    key={tipId}
                    id={tipId}
                    label={section.tipLabels[index]}
                    error={errors[tipId]}
                    value={development[section.key]?.tips?.[index]?.[activeLocale] ?? ""}
                    onChange={(value) => setDevTip({ section: section.key, index, locale: activeLocale, value })}
                  />
                );
              })}
            </div>
          );
        })}
        {saveButton(handleSaveDevelop, savingDevelop)}
      </section>
    </>
  );

  const noteDescription = !hasAnalysisRow ? (
    "This scale has no reporting content yet. Saving will create content shared by every test that uses this scale, which you can then make test specific."
  ) : scale.isTestSpecific ? (
    "This scale is test specific. Changes to these details will be reflected only in this test."
  ) : (
    <div className={style.noteBody}>
      <span>
        This scale is shared with other tests. Changes to these details will be reflected on all tests that use this
        scale. You can make this scale test-specific but remember this action cannot be undone.
      </span>
      <Button buttonSize={ButtonSize.XSmall} onClick={() => setConvertOpen(true)}>
        convert to test specific
      </Button>
    </div>
  );

  return (
    <div className={style.scaleEditor}>
      <h2 className={style.scaleTitle}>
        {scale.name} | {scale.abbr}
      </h2>

      <InfoNote>{noteDescription}</InfoNote>

      {panes}

      <Divider />

      <ConvertToTestSpecificModal
        isOpen={isConvertOpen}
        isLoading={isConverting}
        scaleAbbr={scale.abbr}
        onCancel={() => setConvertOpen(false)}
        onConfirm={handleConvert}
      />
    </div>
  );
}
